<template>
    <v-card outlined class="outlined mb-6 elevation-0 rounded-lg">
      <v-toolbar
        color="transparent"
        flat
        class="text-h5 mb-2 rounded-0 py-1"
      >
        <v-layout justify-space-between align-center class="mt-4">
          <v-flex>Current reservations</v-flex>
          <v-flex>
            <v-layout justify-end align-center>
              <app-input
                data-testid="facility-current-reservation-input"
                class="facility-current-reservation-input pt-3 pb-2 mr-4"
                v-if="searchOn"
                align-with-title
                v-model="searchValue"
                label="Search Reservations"
                :hint="!searchValue ? 'Name, Email, etc...' : 'Click X to reset the list'"
                persistent-hint
                clearable
                :useValidation = "false"
              >
                Search
              </app-input>
              <v-btn
                data-testid="facility-current-reservation-search-btn"
                class="facility-current-reservation-search-btn"
                icon
                @click="toggleSearch"
              >
                <v-icon>search</v-icon>
              </v-btn>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :items="reservations"
        :loading="reservationsLoading"
        :search="searchValue"
        hide-default-footer
        disable-pagination
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        fluid
        :no-data-text="reservationsLoading ? 'Loading reservations...' : 'No reservations found'"
        class="mt-4"
      >
        <template slot="item" slot-scope="props">
          <tr>
          <td class="py-1">{{ props.item.customer_first_name }}</td>
          <td class="py-1">{{ props.item.customer_last_name }}</td>
          <td class="py-1">{{ addDashes(props.item.customer_phone) }}</td>
          <td class="py-1">{{ props.item.customer_email }}</td>
          <td class="py-1">{{ props.item.type == 1 ? "Lead" : "Reservation" }}</td>
          <td class="py-1">${{ parseFloat(props.item.quoted_price).toFixed(2) }}</td>
          <td class="py-1">{{ props.item.move_in_date ? formatDateUTC(props.item.move_in_date) : "—" }}</td>
          <td class="py-1">{{ props.item.promotion ? props.item.promotion.text : "No promotion" }}</td>
          <td class="py-1">
            <v-btn
              data-testid="facility-current-reservation-manage-btn"
              class="facility-current-reservation-manage-btn info mr-2 white--text"
              small
              outlined
              @click.stop="openManageModal(props.item)"
            >
              Manage
            </v-btn>
            <v-btn
              data-testid="facility-current-reservation-convert-btn"
              class="facility-current-reservation-convert-btn primary white--text"
              small
              outlined
              @click.stop="initializeConversion(props.item)"
            >
              Convert
            </v-btn>
          </td>
          </tr>
        </template>
      </v-data-table>
      <convert-lead
      :convertLeadModal="convertLeadModal"
      :lead="lead"
      :unitFeatures="unitFeatures"
      @collapse="closeConvertLeadModal"
      @updateMoveIn="updateMoveIn($event)"
      @updateFollowUp="updateFollowUp($event)"
      @convertToRental="convertToRental($event)"
    />
    <manage-reservation
      v-model="manageModal"
      :facility="facility"
      :reservation="reservation"
      :manageModal="manageModal"
      :unitFeatures="unitFeatures"
      @reservationUpdated="reservationUpdated($event)"
      @tenantUpdated="$emit('tenantUpdated')"
      @collapse="closeManageModal"
    />
    <rent-unit
      :rentUnitModal="rentUnitModal"
      :reservation="reservation"
      :unitFeatures="unitFeatures"
      :tpp="tpp"
      :initialCharges="initialCharges"
      :updateNeeded="updateNeeded"
      :availableUnits="availableUnits"
      :rentUnitStep="rentUnitStep"
      @currentStep="setRentUnitStep($event)"
      @setUnit="setUnit($event)"
      @unitUpdated="unitUpdated($event)"
      @collapse="closeRentUnitModal"
    />
    </v-card>
</template>

<script>
/* eslint-disable eqeqeq */
import ConvertLead from "@/components/convert-lead/ConvertLead";
import ManageReservation from "@/components/ManageReservation";
import RentUnit from "@/components/rent-unit/RentUnit";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import { facilityReservationHeaders } from "@/helpers/facilities/facility-reservation-data-table-headers";
import { mapState } from "vuex";
import moment from "moment";

export default {
  props: {
    facility: {
      type: Object,
      required: true
    }
  },

  mixins: [dateMethodMixins],

  data() {
    return {
      infoLoading: false,
      unitFeaturesLoading: false,

      convertLeadModal: false,
      manageModal: false,
      rentUnitModal: false,
      moveInSelect: false,
      followUpSelect: false,

      lead: "",
      reservation: "",

      unit: "",
      unitFeatures: "",
      tpp: [],
      initialCharges: [],
      updateNeeded: false,
      availableUnits: [],
      rentUnitStep: 0,

      notes: [],

      searchOn: false,
      searchValue: "",

      headers: facilityReservationHeaders,
      sortBy: "move_in_date",
      sortDesc: true
    };
  },

  methods: {
    openConvertLeadModal(dealData) {
      this.initializeTenant(dealData);
      this.lead = dealData;
      this.convertLeadModal = true;
      const payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: dealData.unit_id || dealData.unit_id_first_available,
        concession_id: dealData.concessionId
      };
      this.getUnitFeatures(payload);
    },

    openManageModal(dealData) {
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: dealData.unit_id,
        concession_id: dealData.concession_id
      };

      this.reservation = dealData;
      this.manageModal = true;
      this.getUnitFeatures(payload);
      this.getPromotionOptions(payload);
      this.$store.commit("setMoveInCost", null);
    },

    closeConvertLeadModal() {
      this.convertLeadModal = false;
      this.lead = "";
      this.$store.commit("updateInfo", null); // tenant info
    },

    closeManageModal() {
      this.manageModal = false;
      this.reservation = "";
      this.$store.commit("setMoveInCost", null);
      this.$store.commit("updateInfo", null); // tenant info
    },

    closeRentUnitModal() {
      this.rentUnitModal = false;
      this.updateNeeded = false;
      this.$store.commit("updateInfo", null); // tenant info
      this.$store.commit("setDeferPromotion", false);
    },

    convertToRental(dealData) {
      const payload = { key: "leadConversion", value: true };
      this.$store.commit("updateOptionsValue", payload);
      this.openRentUnitModal(dealData);
    },

    openRentUnitModal(dealData) {
      this.$store.commit("setMoveInCost", null);
      if (!dealData.rate && !dealData.quoted_price) {
        this.$store.dispatch("handleQuotedRate", dealData);
      } else {
        this.initializeTenant(dealData);
        this.$store.dispatch("handleQuotedRate", dealData);
        const concessionId = dealData.concession_id || null;
        const payload = {
          facilityId: this.$route.params.facilityId,
          unit_id: dealData.unit_id || dealData.unit_id_first_available,
          concession_id: concessionId,
          move_in_date: moment(dealData.move_in_date).format("YYYY-MM-DD"),
          waiting_id: dealData.waiting_list_id
        };
        this.$store.commit("setUnit", dealData);
        this.$store.commit("updatePromotion", parseFloat(concessionId));
        this.$store.commit("updateMoveInDate", moment(dealData.dNeeded).format("YYYY-MM-DD"));
        this.$store.dispatch("getMerchandise", this.$route.params.facilityId);
        this.$store.dispatch("getSimilarUnits", payload);
        this.$store.dispatch("getMoveInInstructions", this.$route.params.facilityId);

        this.getUnitFeatures(payload)
          .then(resp => {
            if (resp.rented) {
              this.setRentUnitStep(0);
              this.updateNeeded = true;
              this.getUnits();
              this.$store.dispatch(
                "handleAlert",
                "The unit originally tied to this reservation has been rented. " +
                "Please inform the customer and use the provided list to select an available unit."
              );
              this.rentUnitModal = true;
            } else {
              this.getPromotionOptions(payload);
              this.getTenantProtectionPlans();
              this.rentUnitModal = true;
              this.rentUnitStep = 1;
            }
          });
      }
    },

    setRentUnitStep(step) {
      this.rentUnitStep = step;
    },

    getUnits() {
      this.infoLoading = true;
      this.$store.dispatch("getFacility", this.$route.params.facilityId)
        .then(resp => {
          let facility = resp.data.facility;
          this.availableUnits = facility.units.parking ? facility.units.storage.concat(facility.units.parking) : facility.units.storage;
          this.infoLoading = false;
        })
        .catch(err => {
          this.availableUnits = [];
          this.$store.dispatch("handleError", err);
          this.infoLoading = false;
        });
    },

    async getUnitFeatures(payload) {
      this.unitFeaturesLoading = true;
      await this.$store.dispatch("getUnitFeatures", payload)
        .then(resp => {
          this.unitFeatures = resp.data;
          this.unitFeaturesLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.unitFeaturesLoading = false;
        });
      return Promise.resolve(this.unitFeatures);
    },

    getTenantProtectionPlans() {
      this.$store.dispatch("getTenantProtectionPlans", this.$route.params.facilityId)
        .then(resp => {
          this.tpp = resp.data;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getPromotionOptions(payload) {
      this.$store.dispatch("getPromotionOptions", payload);
    },

    setUnit(unit) {
      this.reservation = unit;
    },

    unitUpdated(unit) {
      const concessionId = unit.concession_id || null;
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: unit.unit_id_first_available,
        concession_id: concessionId,
        move_in_date: moment().format("MM/DD/YYYY")
      };
      this.$store.commit("updatePromotion", concessionId);
      this.getUnitFeatures(payload);
      this.getPromotionOptions(payload);
      this.getTenantProtectionPlans();
    },

    reservationUpdated(unit) {
      this.getUnitFeatures(unit);
      this.getPromotionOptions(unit);
      this.$emit("reservationUpdated");
    },

    updateMoveIn(date) {
      this.lead.move_in_date = date;
    },

    updateFollowUp(date) {
      this.lead.followup_at = date;
    },

    toggleSearch() {
      if (this.searchOn === true) {
        this.searchOn = false;
      } else {
        this.searchOn = true;
      }
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    initializeConversion(reservation) {
      this.$store.commit("invalidPromotion", false);
      if (reservation.type == 1) {
        this.openConvertLeadModal(reservation);
      } else {
        this.openRentUnitModal(reservation);
      }
    },

    initializeTenant(dealData) {
      this.$store.dispatch("handleResetTenant");
      let payload = {
        first_name: dealData.customer_first_name || this.$store.state.tenant.info.first_name,
        last_name: dealData.customer_last_name || this.$store.state.tenant.info.last_name,
        phone_number: dealData.customer_phone || this.$store.state.tenant.info.phone_number,
        email: dealData.customer_email || this.$store.state.tenant.info.email,
        street_address: this.$store.state.tenant.info.street_address,
        city: this.$store.state.tenant.info.city,
        state: this.$store.state.tenant.info.state,
        postal_code: this.$store.state.tenant.info.postal_code,
        id_number: this.$store.state.tenant.info.id_number,
        id_type: this.$store.state.tenant.info.id_type,
        id_state: this.$store.state.tenant.info.id_state,
        business_name: this.$store.state.tenant.info.business_name || "",
        is_business: this.$store.state.tenant.info.is_business || false,
        external_id: dealData.tenant_id || this.$store.state.tenant.info.external_id,
        gate_code: this.$store.state.tenant.info.gate_code,
        waiting_id: dealData.waiting_list_id || this.$store.state.tenant.info.waiting_id
      };
      this.$store.commit("updateInfo", payload);
    }
  },

  computed: {
    ...mapState({
      reservations: (state) => state.reservations.reservations,
      reservationsLoading: (state) => state.reservations.reservationsLoading
    })
  },

  components: {
    ConvertLead,
    ManageReservation,
    RentUnit
  }
};
</script>
